import { Button, Spin, notification } from "antd";
import RadioInput from "./components/radio";
import SingleSelect from "./components/singleSelect";
import InputField from "./components/inputField";
import "./genericForm.scss";
import DateInput from "./components/date";
import SectionField from "./components/section";
import MultiInsertField from "./components/multiInsertField";
import { cloneDeep, filter, find, forEach, isEmpty, map } from "lodash";
import { useDynamicFormStore } from "@stores/dynamicFormStore";
import { useEffect, useMemo, useState } from "react";
import CustomField from "./customField";
import MemberDetailsService from "@services/memberDetails.service";
import NumberInputField from "./components/numberInputField";
import membersService from "@services/members.service";
import HealthemSpinner from "@components/healthemSpinner/healthemSpinner";
import SingleSelectWithDynamicOptions from "./components/singleSelectWIthDynamicOptions";
import moment from "moment-timezone";
import PreSelectInputField from "./components/preSelectInputField";

const GenericForm = ({ isHealthPlanValid, transformedHealthPlanName, isPanelActive }: any) => {
  // Sort to display based on the order
  const currentMemberId = membersService?.getCurrentMember?.patient_id;
  const [originalFields, setOriginalFields, modifiedFields, setModifiedFields, clearStore, isInvalidFlag, setIsInvalidFlag, tags, setTags] = useDynamicFormStore((state) => [
    state.originalFields,
    state.setOriginalFields,
    state.modifiedFields,
    state.setModifiedFields,
    state.clearStore,
    state.isInvalidFlag,
    state.setIsInvalidFlag,
    state.tags,
    state.setTags
  ]);

  const [loader, setLoader] = useState(false);
  const patientId = useMemo(() => currentMemberId, [currentMemberId]);
  const [prevPatientId, setPrevPatientId] = useState(patientId);
  const [lastFetchedPlan, setLastFetchedPlan] = useState<string | null>(null);
  
  useEffect(() => {
    if (isPanelActive) {
      fetchHealthPlanField();
    }
  }, [isPanelActive, transformedHealthPlanName]);

  useEffect(() => {    
    if (patientId !== prevPatientId) {
      reset()
      setPrevPatientId(patientId);
    }
  }, [patientId]);

  const reset = () => {
    const resetFields = cloneDeep(originalFields).map((field: any, index: any) => ({
      ...field,
      value: index === 0 ? field.value : null, 
    }));

    const sortedFieldData = resetFields.filter((item: any) => item.defaultDisplay).sort((a: any, b: any) => a.order - b.order);
    setModifiedFields(sortedFieldData);
    setIsInvalidFlag(false);
    Object.keys(tags).forEach((fieldId) => {
      setTags(fieldId, []);
    });
  };

  const fetchOtherFields = (field: any) => {
    if (field?.value) {
      setLoader(true);
      MemberDetailsService.getFieldsForSupplementalFile(field.value).subscribe({
        next: (v: any) => {
          console.log(v);
          setLoader(false);
          setOriginalFields(cloneDeep([field, ...v?.data?.data]));

          const sortedFieldData = filter(cloneDeep([field, ...v?.data?.data]), (item) => item.defaultDisplay).sort((a, b) => a.order - b.order);
          const fieldsToRender = sortedFieldData;
          setModifiedFields(fieldsToRender);
        },
        error: (e) => {
          console.log(e);
          setLoader(false);
        },
      });
    }
  };

  const fetchHealthPlanField = () => {
    if (isHealthPlanValid && transformedHealthPlanName  && transformedHealthPlanName !== lastFetchedPlan){ 
      setLoader(true);
      MemberDetailsService.fetchHealthPlanInput().subscribe({
        next: (v: any) => {
          const field = v?.data?.data;
          setLoader(false);
          field.value = transformedHealthPlanName;
          fetchOtherFields(field);
          setLastFetchedPlan(transformedHealthPlanName);
        },
        error: (e) => {
          console.log(e);
          setLoader(false);
        },
      });
    }
  }

  useEffect(() => {
    return () => {
      clearStore();
    };
  }, [clearStore]);

  const conditionalRules = [
    {
      conditionFieldId: "encounter_type",
      conditionValue: "Institutional",
      targetFieldId: "bill_type",
      action: { isRequired: true },
    },
    {
      conditionFieldId: "measures",
      conditionValue: ["Hemoglobin A1c Control for Patients With Diabetes", "Controlling High Blood Pressure"],
      targetFieldId: "resno",
      action: { isRequired: true },
      
    },
    {
      conditionFieldId: "measures",
      conditionValue:  ["Hemoglobin A1c Control for Patients With Diabetes", "Controlling High Blood Pressure"],
      targetFieldId: "restext",
      action: { isRequired: true },
    },
    {
      conditionFieldIds: ["bp_systolic", "bp_diastolic", "bmi_percentile", "a1c_value"],
      targetFieldId: "event_description",
      action: { isRequired: true },
      triggerCondition: (fields: any) =>
        fields.some((field: any) => field.value !== null && field.value !== ""),
    },
    {
      conditionFieldId: "posnegresult",
      conditionValue:  ["Not Applicable"],
      targetFieldId: "result",
      action: { isRequired: true },  
    },
    {
      conditionFieldId: "posnegresult",
      conditionValue: ["Positive", "Negative"],
      targetFieldId: "result",
      action: { isEditable: false },
    },
    {
      conditionFieldId: "result",
      targetFieldId: "posnegresult",
      action: { isEditable: false },
      triggerCondition: (value: any) => value !== null && value !== "",
    },  
  ];

  const handleRulesOnValueChange = (field: any, value: any) => {
    const _modifiedFields = [...modifiedFields];
    find(modifiedFields, { id: field.id }).value = value;
    let sortedFieldData = _modifiedFields;
    if (field.rules) {
      forEach(field.rules, (rule) => {
        if (rule.value === value) {
          switch (rule.property) {
            case "hide":
              forEach(rule.fieldIds, (fieldId) => {
                const newField = find(_modifiedFields, { displayId: fieldId });
                if (newField) newField.defaultDisplay = false;

                // hiding the subfields of section also in the state array
                if (newField?.inputType === "ACTN-SECTION" && !isEmpty(newField?.subField)) {
                  forEach(newField?.subField, (subField) => {
                    const newSubField = find(_modifiedFields, { displayId: subField });
                    if (newSubField) newSubField.defaultDisplay = false;
                  });
                }
              });
              break;
            case "display":
              forEach(rule.fieldIds, (fieldId) => {
                const newField = find(cloneDeep(originalFields), { displayId: fieldId });
                if (newField) {
                  newField.defaultDisplay = true;
                  _modifiedFields.push(newField);
                }

                // adding the subfields of section also in the state array
                if (newField?.inputType === "ACTN-SECTION" && !isEmpty(newField?.subField)) {
                  forEach(newField?.subField, (subField) => {
                    const newSubField = find(cloneDeep(originalFields), { displayId: subField });
                    newSubField.defaultDisplay = true;
                    _modifiedFields.push(newSubField);
                  });
                }
              });
              break;
          }
        }
      });

      sortedFieldData = filter(_modifiedFields, (item) => item.defaultDisplay).sort((a, b) => a.order - b.order);
    }

    const updatedField = find(_modifiedFields, { id: field.id });
    if (updatedField) {
      updatedField.value = value;
    }

    conditionalRules.forEach((rule) => {
      const targetField = find(_modifiedFields, { displayId: rule.targetFieldId });

      if (rule.conditionFieldId && field.displayId === rule.conditionFieldId) {
        // Single field condition
        if (targetField) {
          let match = false;

          if (rule.triggerCondition) {
            match = rule.triggerCondition(value);
          } else if (Array.isArray(rule.conditionValue)) {
            const selectedValues = value?.split?.("$|")?.map((val: any) => val.trim()) || [];
            match = rule.conditionValue.some((condValue) => selectedValues.includes(condValue));
          } else {
            match = value === rule.conditionValue;
          }
      
          if (rule.action?.hasOwnProperty("isRequired")) {
            targetField.isRequired = match ? rule.action.isRequired : false;
          }
          if (rule.action?.hasOwnProperty("isEditable")) {
            targetField.isEditable = match ? rule.action.isEditable : true;
          }
        }
  
      } else if (rule.conditionFieldIds) {
        // Multi-field condition
        const fieldsToCheck = rule.conditionFieldIds.map((displayId) =>
          find(_modifiedFields, { displayId })
        );
  
        if (targetField) {
          const conditionMet = rule.triggerCondition(fieldsToCheck);
  
          if (rule.action?.hasOwnProperty("isRequired")) {
            targetField.isRequired = conditionMet ? rule.action.isRequired : false;
          }
          if (rule.action?.hasOwnProperty("isEditable")) {
            targetField.isEditable = conditionMet ? rule.action.isEditable : true;
          }
        }
      }
    });

    setModifiedFields(sortedFieldData);
  };

  function allNonNull(array: any[]) {
    return array.every((item) => !isEmpty(find(modifiedFields, { id: item })?.value));
  }

  // Function to check if the array contains at least one non-null value
  function hasNonNullValue(array: any[]) {
    return array?.some((item) => {
      if (Array.isArray(item)) {
        return allNonNull(item);
      } else {
        return !isEmpty(find(modifiedFields, { displayId: item })?.value);
      }
    });
  }

  const showNotification = (message: string) => {
    notification.error({
      message: "Error",
      description: message,
    });
  };
  
  const handleFieldValue = (field: any, result: any) => {
    if (field.inputType === "ACTN-DATE" && field?.validations?.format) {
      result[field.id] = isEmpty(field.value)
        ? null
        : moment(field.value, field.validations.format).format("YYYYMMDD");
    } else {
      result[field.id] = field.value;
    }
  };

  const handleSubmitButton = () => {
    const result: any = {};
    let isInValidForm = false;
    forEach(modifiedFields, (field) => {
      // if any of the mandatory field is empty then show error and exit
      if (field.isRequired && isEmpty(field.value)) {
        if (field.inputType === "ACTN-SECTION" && field?.metaData?.isMandatory) {
          // Check if the parent array contains any null value
          if (!hasNonNullValue(field.metaData.mandatoryRuleFields)) {
            isInValidForm = true;
            showNotification("Please fill all the section fields.");
            return false;
          }
        } else {
          console.log("field", field);
          isInValidForm = true;
          showNotification("Please fill all the mandatory fields.");
          return false;
        }
      } else {
        handleFieldValue(field, result);
      }
    });

    if (!isInValidForm && !isInvalidFlag) {
      console.log("Submit Clicked", result);
      setLoader(true);
      MemberDetailsService.addSupplementalData({ ...result, patient_id: currentMemberId }).subscribe({
        next: (v) => {
          console.log(v);
          setLoader(false);
          notification.success({
            message: "Success",
            description: "Details Successfully Submitted.",
          });
          reset();
        },
        error: (e) => {
          console.log(e);
          setLoader(false);
        },
      });
    }
  };

  return (
    <Spin spinning={loader} indicator={<HealthemSpinner />}>
      <div className="supplementalFileContainer">
        {/* header */}
        <h3>Patient Level Information</h3>
        <div>
          {/*Inner  Section */}
          {map(
            filter(modifiedFields, (field) => !field.isSubField),
            (field: any) => (
              <CustomField key={`${field.id}`} field={field} renderField={renderField} handleRulesOnValueChange={handleRulesOnValueChange} />
            )
          )}
        </div>
        {/* footer of Page */}
        <div className="supplementalFileContainer__body__fields__required f14 w400">
          <span>* Fields are Mandatory</span>
        </div>

        <div className="supplementalFileContainer__footer">
          <Button
            type="text"
            className="supplementalFileContainer__footer__cancel-button f14 w500"
            onClick={() => {
              console.log("Clicked");
              reset();
            }}
          >
            Reset
          </Button>
          <Button
            type="primary"
            onClick={() => {
              handleSubmitButton();
            }}
            disabled={isInvalidFlag}
          >
            Submit
          </Button>
        </div>
      </div>
    </Spin>
  );
};

export const renderField = (field: any, handleRulesOnValueChange: { (field: { rules: any }, value: any): void; (arg0: any, arg1: any): any }) => {
  switch (field.inputType) {
    case "ACTN-SECTION":
      return (
        <SectionField
          title={field.title}
          subField={field.subField}
          metaData={field.metaData}
          isRequired={field.isRequired}
          className={"supplementalFileContainer__body__fields"}
          handleRulesOnValueChange={handleRulesOnValueChange}
        />
      );
    case "ACTN-RADIO":
      return <RadioInput value={field.value} handleRulesOnValueChange={(value: any) => handleRulesOnValueChange(field, value)} options={field.options} rules={field.rules} />;
    case "ACTN-DRPDWN":
      return <SingleSelect value={field.value} placeholder={field.placeholder} handleRulesOnValueChange={(value: any) => handleRulesOnValueChange(field, value)} options={field.options} isEditable={field.isEditable}/>;
    case "ACTN-DRPDWN-DYNAMIC-OPTIONS":
      return <SingleSelectWithDynamicOptions  className={"supplementalFileContainer__body__fields"} value={field.value} placeholder={field.placeholder} handleRulesOnValueChange={(value: any) => handleRulesOnValueChange(field, value)} optionsKey={field.optionsKey} />;
    case "ACTN-DATE":
      return <DateInput value={field.value} validations={field.validations} handleRulesOnValueChange={(value: any) => handleRulesOnValueChange(field, value)} placeholder={field.placeholder}/>;
    case "ACTN-FREETXT":
      return (
        <InputField
          handleRulesOnValueChange={(value: any) => handleRulesOnValueChange(field, value)}
          placeholder={field.placeholder}
          title={field.title}
          value={field.value}
          isRequired={field.isRequired}
          isEditable={field.isEditable}
          validations={field.validations}
          className={"supplementalFileContainer__body__fields"}
        />
      );
    case "ACTN-PRESELECTTXT":
      return (
        <PreSelectInputField
          handleRulesOnValueChange={(value: any) => handleRulesOnValueChange(field, value)}
          placeholder={field.placeholder}
          title={field.title}
          value={field.value}
          isRequired={field.isRequired}
          isEditable={field.isEditable}
          validations={field.validations}
          rule={field.rule}
          className={"supplementalFileContainer__body__fields"}
        />
      );
    case "ACTN-NUMBER":
      return (
        <NumberInputField
          handleRulesOnValueChange={(value: any) => handleRulesOnValueChange(field, value)}
          placeholder={field.placeholder}
          title={field.title}
          value={field.value}
          isRequired={field.isRequired}
          isEditable={field.isEditable}
          validations={field.validations}
          className={"supplementalFileContainer__body__fields"}
        />
      );
    case "ACTN-MULTIINSERT":
      return <MultiInsertField handleRulesOnValueChange={(value: any) => handleRulesOnValueChange(field, value)} validations={field.validations} className={"supplementalFileContainer__body__fields"} displayId={field.displayId} placeholder={field.placeholder}/>;
    default:
      return null;
  }
};

export default GenericForm;
